// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import MobiusAccordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { accordionFooterSectionProps } from "@store/Themes/common/props/accordionFooter";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC, useMemo } from "react";

export type AccordionStyleOption = DataOption<AccordionSectionPresentationProps>;
export interface AccordionStyleOptionMapper {
    default: AccordionStyleOption;
    footer: AccordionStyleOption;
}
export type AccordionStyleOptions = DataOptions<AccordionStyleOption> & AccordionStyleOptionMapper;
export type AccordionStyle = keyof AccordionStyleOptionMapper;

export const accordionStyleOptions: AccordionStyleOptions = {
    default: { id: "default", displayName: "Default", isDefault: true },
    footer: { id: "footer", displayName: "Outline", value: accordionFooterSectionProps },
};

const enum fields {
    headingLevel = "headingLevel",
    sections = "sections",
    style = "style",
}

const enum sectionFields {
    expanded = "expanded",
    id = "id",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.headingLevel]: number;
        [fields.sections]: [
            {
                fields: {
                    [sectionFields.expanded]: boolean;
                    [sectionFields.id]: string;
                    [sectionFields.title]: string;
                };
            },
        ];
        [fields.style]: string;
    };
}

export interface AccordionStyles {
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const accordionStyles: AccordionStyles = {};

const Accordion: FC<OwnProps> = ({ fields: { headingLevel = 0, sections, style }, id }) => {
    const styles = useMemo(() => {
        return mergeProps<AccordionStyles>(accordionStyles, {
            accordionSection: getDataOptionValue(accordionStyleOptions, style),
        });
    }, [accordionStyles, style]);

    const accordionSections = sections.map(({ fields: section }, index) => {
        return (
            <ManagedAccordionSection
                key={section.id || index}
                initialExpanded={section.expanded}
                title={section.title}
                {...styles.accordionSection}
            >
                <Zone contentId={id} zoneName={`AccordionSection-${section.id || index}`} />
            </ManagedAccordionSection>
        );
    });

    if (!accordionSections.length) {
        return null;
    }

    return (
        <MobiusAccordion headingLevel={headingLevel} {...styles.accordion}>
            {accordionSections}
        </MobiusAccordion>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: Accordion,
    definition: {
        group: "Basic",
        displayName: "Accordion",
        icon: "list-dropdown",
        fieldDefinitions: [
            {
                name: fields.headingLevel,
                displayName: "Heading Level",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: null,
                tooltip:
                    "Semantic heading level for accessibility (1-6), only set if accordions are to be treated as page headings",
                validate: x => (typeof x === "number" && (x < 1 || x > 6) ? "Value must be between 1 and 6" : ""),
            },
            {
                ...getDataOptionsFieldDefinition(accordionStyleOptions),
                name: fields.style,
                displayName: "Style",
                fieldType: "Contextual",
                hideEmptyOption: true,
            },
            {
                name: fields.sections,
                editorTemplate: "ListField",
                getDisplay: x => x.fields.title,
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: sectionFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: sectionFields.expanded,
                        displayName: "Expand by default",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: sectionFields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
